import { cva } from "class-variance-authority";
import type { VariantProps } from "class-variance-authority";
import { Slot } from "radix-ui";
import type { ComponentPropsWithoutRef, Ref } from "react";

import { cn } from "../cn.ts";
import { LINK_LOOK } from "../layout/link-look.ts";

// A trailing arrow steps forward on hover, so a button that goes somewhere says so.
const button = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md font-medium border border-transparent transition-[color,background-color,box-shadow,transform] duration-150 outline-none focus-visible:border-ring focus-visible:ring-2 focus-visible:ring-ring/30 select-none cursor-pointer disabled:pointer-events-none disabled:opacity-50 active:translate-y-px [&_svg]:size-4 [&_svg]:shrink-0 [&_[data-icon=arrow-right]]:transition-transform [&_[data-icon=arrow-right]]:duration-(--motion-slow) [&_[data-icon=arrow-right]]:ease-glide hover:[&_[data-icon=arrow-right]]:translate-x-1",
  {
    variants: {
      variant: {
        primary: "bg-primary text-primary-fg hover:bg-primary/80",
        secondary: "border-border bg-surface text-fg hover:bg-input/50",
        ghost: "text-fg hover:bg-bg-subtle",
        inverted: "bg-inverted text-inverted-fg hover:opacity-90",
        soft: "bg-primary-soft text-primary-soft-fg hover:bg-primary-soft/70",
        danger: "bg-danger/10 text-danger hover:bg-danger/20",
        link: "text-primary underline-offset-4 hover:underline px-0 h-auto",
        /** What opens something from inside running text: a real button that looks like the link beside it. */
        "text-link": `${LINK_LOOK} h-auto rounded-sm border-0 px-0 text-sm`,
        /** A suggestion on a glowing hero: translucent, round, quiet. */
        glass:
          "rounded-full border-border bg-surface/60 text-fg-muted backdrop-blur-md hover:bg-surface/90 hover:text-fg",
        /** A sortable column's header: the header's own small capitals, quiet until hovered or
         * sorted by (`aria-pressed`); the negative margin keeps its text on the column's edge. */
        column:
          "-mx-2.5 font-semibold tracking-wide uppercase text-fg-muted hover:bg-bg-subtle hover:text-fg aria-pressed:text-fg",
        /** A filter not yet set: a dashed outline that turns solid once it holds a value. */
        filter:
          "border-dashed border-border-strong bg-transparent text-fg hover:bg-bg-subtle aria-pressed:border-solid aria-pressed:border-border aria-pressed:bg-surface",
        /** A menu's plain trigger: words between two icons, opening on a hover or a focus. */
        menu: "cursor-pointer gap-2 rounded-full px-3 py-1.5 text-sm text-fg-muted hover:text-fg focus-visible:text-fg group-focus-within/reveal:text-fg [&_svg]:size-3.5",
        /** A share icon: bare, in the muted ink until hovered or focused, as warike.tech's share row. */
        share: "rounded-md text-fg-muted hover:text-fg focus-visible:text-fg",
        /** A trigger that is a Badge, which draws everything itself. */
        chip: "cursor-pointer rounded-full text-xs [&>span]:border-border",
      },
      size: {
        /** The variant holds every measure. */
        bare: "h-auto",
        sm: "h-7 gap-1.5 px-2.5 text-xs [&_svg]:size-3.5",
        md: "h-8 gap-1.5 px-3 text-sm",
        lg: "h-10 px-4 text-sm",
        /** One icon, nothing else: a close, a copy, a bell. Square, no label padding. */
        icon: "size-7 p-0 [&_svg]:size-4",
        /** A share icon's target: 44 square on a phone, 34 from a tablet up, the icon 18 at both. */
        share: "size-11 p-0 sm:size-[34px] [&_svg]:size-[18px]",
      },
      block: { true: "w-full" },
    },
    defaultVariants: { variant: "secondary", size: "md" },
  },
);

export type ButtonProps = Omit<ComponentPropsWithoutRef<"button">, "className" | "style"> &
  VariantProps<typeof button> & {
    /** Render the single child (an anchor) with the button's look. */
    readonly asChild?: boolean;
    readonly ref?: Ref<HTMLButtonElement>;
  };

export function Button({ variant, size, block, asChild, type, ...rest }: ButtonProps) {
  const Element = asChild ? Slot.Root : "button";
  return (
    <Element
      className={cn(button({ variant, size, block }))}
      {...(asChild ? {} : { type: type ?? "button" })}
      {...rest}
    />
  );
}
